import { useQuery } from '@tanstack/react-query'
import apiClient from '@/api/client'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCurrency } from '@/contexts/CurrencyContext'
import { clockTime } from '@/lib/onlineOrderTiming'
import { CUSTODY_LABEL } from '@/lib/riderLinks'
import type { RiderCash } from '@/types/rider'

// "Money I hold" (F-DLV-N1): every cash or own-wallet collection the rider has
// not handed in yet, line by line, so the count at the counter is no surprise.
export function RiderCashSheet({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { formatCurrency } = useCurrency()
  const { data, isLoading } = useQuery({
    queryKey: ['rider', 'me', 'cash'],
    queryFn: () => apiClient.getRiderMyCash(),
    enabled: open,
  })
  const cash = data?.data as RiderCash | undefined

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Money you hold</DialogTitle>
          <DialogDescription>Hand this over at the counter at the end of your shift.</DialogDescription>
        </DialogHeader>
        {isLoading || !cash ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-lg border p-2.5">
                <p className="text-xs text-muted-foreground">Cash</p>
                <p className="font-semibold">{formatCurrency(cash.held.cash)}</p>
              </div>
              <div className="rounded-lg border p-2.5">
                <p className="text-xs text-muted-foreground">In your wallet</p>
                <p className="font-semibold">{formatCurrency(cash.held.wallet)}</p>
              </div>
            </div>
            {cash.collections.length === 0 ? (
              <p className="py-2 text-center text-sm text-muted-foreground">Nothing to hand in.</p>
            ) : (
              <ul className="max-h-72 divide-y overflow-y-auto rounded-lg border text-sm">
                {cash.collections.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span>
                      #{c.order_number}
                      <span className="block text-xs text-muted-foreground">
                        {CUSTODY_LABEL[c.custody]}
                        {c.payment_sub_method ? ` · ${c.payment_sub_method}` : ''}
                        {c.reference_number ? ` · ${c.reference_number}` : ''} · {clockTime(c.collected_at)}
                      </span>
                    </span>
                    <span className="font-medium">{formatCurrency(c.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
